/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";

import {
  copyToClipboard,
  copyDescriptionCells,
} from "../../assets/projectContentHelper.js";
import "./DescriptionCopyMenu.css";

// Two items plus the menu's own padding — enough to decide which way to open.
const MENU_HEIGHT = 108;

/**
 * "Copy for bulk import" menu for a description editor.
 *
 * Owns the menu and the copy itself so CreateGoal and the project table's
 * quick-edit don't each carry their own copy of the clipboard handling. The
 * consumer supplies two getters:
 *   getCode()   -> the shown language's description code, "" when empty
 *   getFields() -> one code per template Description column, null when the
 *                  required Default column is empty
 */
class DescriptionCopyMenu extends Component {
  constructor(props) {
    super(props);
    this.state = { open: false, status: "", code: "", dropUp: false };
    this.anchorRef = React.createRef();
    this.toggleOpen = this.toggleOpen.bind(this);
    this.copyThisLanguage = this.copyThisLanguage.bind(this);
    this.copyAllLanguages = this.copyAllLanguages.bind(this);
  }

  componentDidUpdate(prevProps) {
    // Consumers pass a value that changes when the copied content would no
    // longer match — a language switch, for instance — so a stale
    // confirmation doesn't linger.
    if (prevProps.resetKey !== this.props.resetKey && this.state.status) {
      this.setState({ status: "", code: "" });
    }
  }

  t = (key, fallback) => {
    const block =
      this.props.languageObj && typeof this.props.languageObj === "object"
        ? this.props.languageObj
        : {};
    return block[key] || fallback;
  };

  /**
   * The trigger sits at the bottom of the editor, which is often the last
   * thing in a scrolling card -- so a downward menu gets clipped by that
   * card's overflow. Measure against the nearest clipping ancestor (falling
   * back to the viewport) and open upwards when there isn't room below.
   */
  shouldDropUp() {
    const el = this.anchorRef.current;
    if (!el || typeof window === "undefined") return false;
    const rect = el.getBoundingClientRect();
    let limit = window.innerHeight;
    let node = el.parentElement;
    while (node && node !== document.body) {
      const cs = window.getComputedStyle(node);
      const overflow = cs.overflowY + " " + cs.overflow;
      if (/auto|scroll|hidden/.test(overflow)) {
        limit = Math.min(limit, node.getBoundingClientRect().bottom);
        break;
      }
      node = node.parentElement;
    }
    return limit - rect.bottom < MENU_HEIGHT;
  }

  toggleOpen() {
    // Opening clears the previous result, so the menu never sits under a
    // confirmation from an earlier copy.
    this.setState((prev) => ({
      open: !prev.open,
      dropUp: prev.open ? prev.dropUp : this.shouldDropUp(),
      status: prev.open ? prev.status : "",
      code: prev.open ? prev.code : "",
    }));
  }

  async copyThisLanguage() {
    const code = this.props.getCode();
    if (!code) {
      this.setState({ open: false, status: "empty", code: "" });
      return;
    }
    const copied = await copyToClipboard(code);
    this.setState({
      open: false,
      status: copied ? "copied" : "failed",
      code: copied ? "" : code,
    });
  }

  async copyAllLanguages() {
    const fields = this.props.getFields();
    if (!fields) {
      this.setState({ open: false, status: "emptyDefault", code: "" });
      return;
    }
    const copied = await copyDescriptionCells(fields);
    this.setState({
      open: false,
      status: copied ? "copiedAll" : "failed",
      code: copied ? "" : fields.join("\t"),
    });
  }

  renderStatus() {
    const { status, code } = this.state;
    if (!status) return null;

    if (status === "failed") {
      return (
        <div className="DescriptionCopyMenu-fallback">
          <div className="DescriptionCopyMenu-error">
            {this.t(
              "descriptionCodeCopyFailed",
              "Could not copy automatically. Select the code below and copy it manually."
            )}
          </div>
          <textarea
            className="DescriptionCopyMenu-code"
            readOnly
            rows={4}
            value={code}
            onFocus={(e) => e.target.select()}
          />
        </div>
      );
    }

    const messages = {
      copied: [
        "descriptionCodeCopied",
        "Copied.",
        "DescriptionCopyMenu-ok",
      ],
      copiedAll: [
        "allDescriptionCodesCopied",
        "Copied all languages — paste into the Description (Default) cell to fill all four columns.",
        "DescriptionCopyMenu-ok",
      ],
      empty: [
        "descriptionCodeEmpty",
        "This language has no description to copy.",
        "DescriptionCopyMenu-warn",
      ],
      emptyDefault: [
        "allDescriptionCodesEmptyDefault",
        "Add a description for the Default language before copying all languages.",
        "DescriptionCopyMenu-warn",
      ],
    };
    const entry = messages[status];
    if (!entry) return null;
    return <div className={entry[2]}>{this.t(entry[0], entry[1])}</div>;
  }

  render() {
    const { open, dropUp } = this.state;
    return (
      <div className="DescriptionCopyMenu">
        <div className="DescriptionCopyMenu-bar">
          <div className="DescriptionCopyMenu-anchor" ref={this.anchorRef}>
            <button
              type="button"
              className="DescriptionCopyMenu-trigger"
              onClick={this.toggleOpen}
              aria-expanded={open}
              aria-haspopup="menu"
            >
              {this.t("copyForBulkImport", "Copy for bulk import")}
            </button>

            {open ? (
              <div
                className={
                  "DescriptionCopyMenu-menu" +
                  (dropUp ? " DescriptionCopyMenu-menu--up" : "")
                }
                role="menu"
              >
                <button
                  type="button"
                  role="menuitem"
                  className="DescriptionCopyMenu-item"
                  onClick={this.copyThisLanguage}
                >
                  {this.t("copyThisLanguage", "Copy in this language")}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="DescriptionCopyMenu-item"
                  onClick={this.copyAllLanguages}
                >
                  {this.t(
                    "copyAllLanguages",
                    "Copy all supported languages"
                  )}
                </button>
              </div>
            ) : null}
          </div>
        </div>
        {this.renderStatus()}
      </div>
    );
  }
}

DescriptionCopyMenu.propTypes = {
  languageObj: PropTypes.object,
  getCode: PropTypes.func.isRequired,
  getFields: PropTypes.func.isRequired,
  resetKey: PropTypes.any,
};

export default DescriptionCopyMenu;
